import { useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, NavLink } from 'react-router-dom';
import { format } from 'date-fns';
import { useConversations } from '@/hooks/useConversations';
import { useChat } from '@/hooks/useChat';
import { useWorkspace } from '@/context';
import { useSavedActions } from '@/hooks/useSavedActions';
import { ROUTES, conversationPath, conversationPathWithWorkflow } from '@/config/routes';
import { isDeviceSetupConversation } from '@/utils/conversationWorkflow';
import { ChatInput } from '@/components/Workspace/ChatInput';
import { renderWithMentions } from '@/utils/renderMentions';

function ConversationsPage() {
  const { conversationId } = useParams();
  const { effectiveRole, effectiveUser } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { actions: savedActions } = useSavedActions(effectiveRole);

  const pendingSendEncoded = searchParams.get('pendingSend');
  const { conversations, isLoading, error, fetchConversations } = useConversations(effectiveRole);

  const {
    messages,
    isStreaming,
    isLoadingMessages,
    error: threadError,
    sendMessage,
    reloadMessages,
  } = useChat(conversationId, {
    persistMessages: Boolean(conversationId),
    onPersistComplete: fetchConversations,
  });

  const consumedPendingSend = useRef<string | null>(null);

  useEffect(() => {
    if (!conversationId || !pendingSendEncoded || isLoadingMessages) return;

    const token = `${conversationId}:${pendingSendEncoded}`;
    if (consumedPendingSend.current === token) return;
    consumedPendingSend.current = token;

    let decoded: string;
    try {
      decoded = decodeURIComponent(pendingSendEncoded);
    } catch {
      decoded = pendingSendEncoded;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('pendingSend');
        return next;
      },
      { replace: true }
    );

    sendMessage(decoded);
  }, [conversationId, pendingSendEncoded, isLoadingMessages, sendMessage, setSearchParams]);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === conversationId),
    [conversations, conversationId]
  );

  return (
    <section className="conversations-page">
      <aside className="conversations-page__list-panel" aria-label="Conversation list">
        <header className="conversations-page__list-header">
          <h1 className="conversations-page__title">Conversations</h1>
          <p className="conversations-page__subtitle">
            View your chat history and open a thread to continue.
          </p>
          {error ? (
            <section className="conversations-page__banner conversations-page__banner--error" role="alert">
              <p>We couldn&apos;t load conversations. Is the API running on port 5000?</p>
              <button type="button" className="conversations-page__retry" onClick={() => void fetchConversations()}>
                Retry
              </button>
            </section>
          ) : null}
        </header>

        {isLoading ? (
          <p className="conversations-page__muted">Loading conversations…</p>
        ) : conversations.length === 0 ? (
          <section className="conversations-page__empty">
            <p>No conversations yet.</p>
            <button type="button" className="conversations-page__linkish" onClick={() => navigate(effectiveRole === 'operator' ? ROUTES.OPERATOR_WORKSPACE : ROUTES.NOVICE_WORKSPACE)}>
              Go to workspace to start one
            </button>
          </section>
        ) : (
          <ul className="conversations-page__list">
            {conversations.map((c) => (
              <li key={c.id}>
                <NavLink
                  to={
                    isDeviceSetupConversation(c.id)
                      ? conversationPathWithWorkflow(c.id, 'device-setup', effectiveRole)
                      : conversationPath(c.id, effectiveRole)
                  }
                  className={({ isActive }) =>
                    `conversations-page__list-item${isActive ? ' conversations-page__list-item--active' : ''}`
                  }
                >
                  <span className="conversations-page__list-item-title">{c.title}</span>
                  <span className="conversations-page__list-item-meta">
                    {format(new Date(c.updatedAt), 'MMM d, yyyy')} · {c.messageCount} messages
                  </span>
                  {c.lastMessage ? (
                    <span className="conversations-page__list-item-preview">{c.lastMessage}</span>
                  ) : null}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="conversations-page__thread" aria-label="Conversation thread">
        {!conversationId ? (
          <section className="conversations-page__placeholder">
            <h2 className="conversations-page__thread-title">Select a conversation</h2>
            <p className="conversations-page__muted">
              Choose a thread from the list, or go to workspace to send a message and start chatting.
            </p>
          </section>
        ) : (
          <>
            <header className="conversations-page__thread-header">
              <h2 className="conversations-page__thread-title">
                {activeConversation?.title ?? 'Conversation'}
              </h2>
              {activeConversation ? (
                <p className="conversations-page__muted">
                  Updated {format(new Date(activeConversation.updatedAt), 'PPp')}
                </p>
              ) : null}
            </header>

            {threadError ? (
              <section className="conversations-page__banner conversations-page__banner--error" role="alert">
                <p>{threadError.message}</p>
                <button type="button" className="conversations-page__retry" onClick={() => void reloadMessages()}>
                  Retry
                </button>
              </section>
            ) : null}

            {isLoadingMessages ? (
              <p className="conversations-page__muted">Loading messages…</p>
            ) : (
              <>
                <ul className="conversations-page__messages">
                  {messages.map((m) => (
                    <li key={m.id} className={`conversations-page__msg conversations-page__msg--${m.type}`}>
                      <span className="conversations-page__msg-label">{m.type}</span>
                      <p className="conversations-page__msg-body">{renderWithMentions(m.content)}</p>
                      <time className="conversations-page__msg-time" dateTime={m.createdAt}>
                        {format(new Date(m.createdAt), 'p')}
                      </time>
                    </li>
                  ))}
                </ul>
                {messages.length === 0 ? (
                  <p className="conversations-page__muted">No messages in this thread yet.</p>
                ) : null}
              </>
            )}

            <section className="conversations-page__composer">
              <ChatInput
                userSite={effectiveUser.site}
                showTabs={false}
                savedActions={savedActions}
                onSend={(msg, opts) => void sendMessage(msg, opts)}
                disabled={isStreaming || isLoadingMessages}
              />
            </section>
          </>
        )}
      </section>
    </section>
  );
}

export default ConversationsPage;
